import { evaluateRules } from './evaluator.js';
import type { RelationshipContextType } from './relationshipContext.js';
import { extractRelationshipTimingFeatures, type RelationshipTimingContextBundle } from './relationshipTimingContext.js';
import { resolveInterpretation } from './resolver.js';
import { assertRulePackIntegrity } from './rulePackValidation.js';
import type { InterpretationRule } from './rules.js';
import type { TemplateDefinition } from './template.js';

export const TIMING_LAYERING_METHODOLOGY_ID = 'TIMING-META-LAYERING-001' as const;

export interface RelationshipTimingRulePack {
  id: string;
  version: string;
  compatibleCalculationRulesets: readonly string[];
  compatibleRelationshipTypes: readonly RelationshipContextType[];
  methodologyIds: readonly string[];
  rules: readonly InterpretationRule[];
}

export interface RelationshipTimingTemplatePack {
  id: string;
  version: string;
  templates: Readonly<Record<string, TemplateDefinition>>;
}

export interface GeneratedRelationshipTimingReport {
  relationshipType: RelationshipContextType;
  relationshipContextFingerprint: string;
  timingContextFingerprint: string;
  targetSolarDate: string;
  targetLunarDate: string;
  targetTimeIndex: number;
  interpretationStatus: 'research_pending' | 'source_backed_rules_available';
  resolved: ReturnType<typeof resolveInterpretation>;
  versions: {
    relationshipTimingRulePack: string;
    methodologyIds: readonly string[];
    interpretationRules: readonly string[];
    templatePack: string;
  };
  disclosure: string;
}

export const RELATIONSHIP_TIMING_RULE_PACK_0_1_0: RelationshipTimingRulePack = Object.freeze({
  id: 'INTERP-RELATIONSHIP-TIMING-CONTEXT',
  version: '0.1.0',
  compatibleCalculationRulesets: Object.freeze(['CALC-COMMON-BOOK']),
  compatibleRelationshipTypes: Object.freeze<RelationshipContextType[]>(['partner', 'siblings', 'parent_to_child', 'child_to_parent']),
  methodologyIds: Object.freeze([TIMING_LAYERING_METHODOLOGY_ID]),
  rules: Object.freeze([]),
});

export const RELATIONSHIP_TIMING_TEMPLATE_PACK_0_1_0: RelationshipTimingTemplatePack = Object.freeze({
  id: 'TEMPLATE-RELATIONSHIP-TIMING-ZHTW',
  version: '0.1.0',
  templates: Object.freeze({}),
});

export function generateRelationshipTimingReport(
  context: RelationshipTimingContextBundle,
  personACalculationRuleset: string,
  personBCalculationRuleset: string,
  rulePack: RelationshipTimingRulePack = RELATIONSHIP_TIMING_RULE_PACK_0_1_0,
  templatePack: RelationshipTimingTemplatePack = RELATIONSHIP_TIMING_TEMPLATE_PACK_0_1_0,
): GeneratedRelationshipTimingReport {
  if (!rulePack.compatibleRelationshipTypes.includes(context.relationshipType)) {
    throw new Error(`Relationship timing rule pack ${rulePack.id}@${rulePack.version} is not compatible with relationship type ${context.relationshipType}`);
  }
  for (const [role, ruleset] of [['A', personACalculationRuleset], ['B', personBCalculationRuleset]] as const) {
    const separator = ruleset.indexOf('@');
    if (separator <= 0) throw new Error(`Invalid calculation ruleset version string for person ${role}: ${ruleset}`);
    const rulesetId = ruleset.slice(0, separator);
    if (!rulePack.compatibleCalculationRulesets.includes(rulesetId)) {
      throw new Error(`Relationship timing rule pack ${rulePack.id}@${rulePack.version} is not compatible with person ${role} calculation ruleset ${ruleset}`);
    }
  }
  assertRulePackIntegrity(rulePack, templatePack);
  const features = extractRelationshipTimingFeatures(context);
  const traces = evaluateRules(features, rulePack.rules).filter((trace) => trace.rule.topic === 'timing');
  const resolved = resolveInterpretation('timing', traces);
  return Object.freeze({
    relationshipType: context.relationshipType,
    relationshipContextFingerprint: context.relationshipContextFingerprint,
    timingContextFingerprint: context.timingContextFingerprint,
    targetSolarDate: context.targetSolarDate,
    targetLunarDate: context.targetLunarDate,
    targetTimeIndex: context.targetTimeIndex,
    interpretationStatus: rulePack.rules.length === 0 ? 'research_pending' : 'source_backed_rules_available',
    resolved,
    versions: Object.freeze({
      relationshipTimingRulePack: `${rulePack.id}@${rulePack.version}`,
      methodologyIds: Object.freeze([...rulePack.methodologyIds].sort()),
      interpretationRules: Object.freeze([...new Set(traces.map((trace) => `${trace.rule.id}@${trace.rule.version}`))].sort()),
      templatePack: `${templatePack.id}@${templatePack.version}`,
    }),
    disclosure: rulePack.rules.length === 0
      ? '雙人同期間運限目前只提供客觀疊加 Evidence；尚無通過 Source Registry 與 Regression Gate 的正式雙人運限判讀規則，因此不產生事件預言、吉凶總分或相合率。'
      : '雙人同期間運限只輸出已驗證且實際觸發的來源規則；不宣稱事件必然發生，也不提供相合率或事件機率。',
  });
}
